import { ArrowRightIcon, Button, Table, TableColumns, Tooltip } from '@bp/ui-components';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useColumnsSort } from '../../../hooks/useColumnsSort';
import { useHiddenColumns } from '../../../hooks/useHiddenColumns';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useUserConfigContext } from '../../../hooks/useUserConfigContext';
import { SortDirection, useSubjectsListQuery } from '../../../types/planung-graphql-client-defs';
import { useCurriculum } from '../hooks/useCurriculum';

type CurriculumSubjectTableType = {
  name: string;
  subjectUuid: string;
  subjectHours: string;
  studentHours: string;
  deputat: string;
};

type CurriculumSubjectsTableProps = {
  curriculumUuid: string | undefined;
  onNavigate: (subjectUuid: string) => void;
};

export const CurriculumSubjectsTable = observer(({ curriculumUuid, onNavigate }: CurriculumSubjectsTableProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const currentSchoolYear = useUserConfigContext().selectedSchoolYear;
  const subjectContext = useMemorizedCacheTag('SUBJECTS');

  const { columnVisibility, saveColumnVisibility } = useHiddenColumns('curriculumSubject-table');
  const { sorting, saveSorting } = useColumnsSort('curriculumSubject-table');

  const { tableData } = useCurriculum({
    versionUuid: curriculumUuid ?? '',
  });

  const [{ data: subjectsData }] = useSubjectsListQuery({
    variables: {
      where: {
        organization: {
          uuid: pimAuthClaims.getOrganizationUuid(),
        },
      },
      options: {
        sort: [{ name: SortDirection.Asc }],
      },
    },
    context: subjectContext,
  });

  const data: CurriculumSubjectTableType[] =
    subjectsData?.subjects.map((subject) => {
      const itemData = tableData.filter((c) => c.subjectUuid === subject.uuid);
      return {
        subjectUuid: subject.uuid,
        name: subject.name ?? '',
        subjectHours: itemData.reduce((val, cur) => val + (cur.curriculumInfo?.studentCards ?? 0.0), 0.0).toFixed(2),
        studentHours: itemData.reduce((val, cur) => val + (cur.curriculumInfo?.studentHours ?? 0.0), 0.0).toFixed(2),
        deputat: itemData.reduce((val, cur) => val + (cur.curriculumInfo?.deputat ?? 0.0), 0.0).toFixed(2),
      };
    }) ?? [];

  const columns: TableColumns<CurriculumSubjectTableType>[] = [
    {
      header: t('common.name'),
      id: 'name',
      accessorKey: 'name',
      size: 400,
      cell: (cell) => {
        return <Tooltip content={cell.row.original.name}>{cell.row.original.name}</Tooltip>;
      },
    },
    {
      header: t('subjectHours.title', { count: 2 }),
      accessorKey: 'subjectHours',
      id: 'subjectHours',
      size: 200,
    },
    {
      header: t('subjectHours.studentHours'),
      accessorKey: 'studentHours',
      id: 'studentHours',
      size: 200,
    },
    {
      header: t('deputate.title', { count: 1 }),
      accessorKey: 'deputat',
      id: 'deputat',
      size: 200,
    },
  ];

  return (
    <Table<CurriculumSubjectTableType>
      showBorderRadius
      showRowHover
      showShadow
      canScroll
      minHeight={600}
      columns={columns}
      data={data}
      sorting={sorting}
      onSortingChange={saveSorting}
      isOnWhite={false}
      showVisibility
      showActionBar
      actionBarSettings={{
        showPrintButton: true,
      }}
      columnVisibility={columnVisibility}
      onColumnVisibilityChange={saveColumnVisibility}
      printerSettings={{
        headline: pimAuthClaims.getProfile()?.organization.name,
        subline: `${t('curriculum.title', { count: 2 })} - ${t('subject.title', { count: 2 })} - ${t('common.schoolYear')} ${currentSchoolYear?.shortName}`,
        filename: `${t('curriculum.title', { count: 2 })}_${currentSchoolYear?.shortName}`,
      }}
      onRowClick={(e, row) => onNavigate(row.original.subjectUuid)}
      lastCol={(row) => {
        return (
          <Button
            hierarchy='ghost'
            onClick={() => onNavigate(row.original.subjectUuid)}
            icon={<ArrowRightIcon className='small' />}
          />
        );
      }}
    />
  );
});
